<script setup lang="ts">
import { computed } from 'vue'
import type { Filter, FilterValue, RangeValue } from '@/types/api/reports'
import GwBtn from '@/components/lib/GwBtn/GwBtn.vue'
import GwDateItem from '@/components/DataTables/Filters/new/items/GwDateItem.vue'
import GwBox from '@/components/lib/GwBox/GwBox.vue'
import GwUiType from '@/components/lib/GwTable/GwUiType.vue'
import { LARGE_LABEL_THRESHOLD } from '@/helpers/constantsNew'

export type Props = {
  item: Filter
}

const props = defineProps<Props>()

export type PickerComponentEmits = {
  'update:filter': [Filter]
}

const emit = defineEmits<PickerComponentEmits>()

const handleUpdate = (value: FilterValue) => {
  const item: Filter = {
    ...props.item,
    value,
    is_active: !!value,
  }

  emit('update:filter', item)
}

const selectedOptions = computed(
  () =>
    [props.item.value || []].flat().map(id => ({
      id,
    })) || [],
)

const maxVisibleItems = computed(() =>
  selectedOptions.value.some(({ id }) => id.toString().length > LARGE_LABEL_THRESHOLD) ||
  selectedOptions.value.length > 2
    ? 1 // long filter field name or more than 2 items
    : 2,
)

const removeOption = opt => {
  const value = Array.isArray(props.item.value)
    ? selectedOptions.value.filter(({ id }) => id !== opt.id).map(i => i.id)
    : null
  handleUpdate(value)
}

const removeOptionsFromEnd = (count: number) => {
  const value = Array.isArray(props.item.value)
    ? selectedOptions.value.slice(0, -count).map(i => i.id)
    : null
  handleUpdate(value)
}
</script>
<template>
  <GwBox
    v-if="item.value && item.filter.ui_value_type === 'date'"
    variant="box_select_pill"
    class="gw_hvr"
    @click="handleUpdate(null)"
  >
    <div
      class="d-flex"
      style="gap: var(--app-gap-1)"
    >
      <GwDateItem
        :item="item.value[0]"
        tstPrefix="date_from"
      />
      -
      <GwDateItem
        :item="item.value[1]"
        tstPrefix="date_to"
      />
    </div>
    <GwBtn
      icon="mdi-close"
      style="position: absolute; right: 0; transform: translate(50%, 0)"
      variant="btn_alert_small"
      class="gw_hvr_content"
    />
  </GwBox>
  <GwBox
    v-else-if="item.filter.filter_ui_type === 'RANGE_SELECT'"
    variant="box_select_pill"
    class="gw_hvr"
    @click="handleUpdate(null)"
  >
    <GwUiType
      :value="item.value as RangeValue"
      :uiType="item.filter.ui_value_type"
    />
    <GwBtn
      icon="mdi-close"
      style="position: absolute; right: 0; transform: translate(50%, 0)"
      variant="btn_alert_small"
      class="gw_hvr_content"
    />
  </GwBox>
  <div
    v-else
    class="d-flex"
    style="gap: var(--app-gap-1)"
  >
    <GwBox
      v-for="opt in selectedOptions.slice(0, maxVisibleItems)"
      :key="opt.id"
      variant="box_select_pill"
      class="f-center position-relative gw_hvr cursor-pointer"
      @click="removeOption(opt)"
    >
      <div
        v-if="item.filter.filter_ui_type === 'COLOR_TAG'"
        class="mdi mdi-circle mdi-18px"
        :style="{
          color: opt.id as string,
        }"
      />
      <GwUiType
        v-else
        :value="opt.id"
        :isFilterValue="true"
        :uiType="item.filter.ui_value_type"
      />
      <GwBtn
        icon="mdi-close"
        style="position: absolute; right: 0; transform: translate(50%, 0)"
        variant="btn_alert_small"
        class="gw_hvr_content"
      />
    </GwBox>

    <GwBox
      v-if="selectedOptions.length > maxVisibleItems"
      variant="box_select_pill"
      class="f-center position-relative gw_hvr cursor-pointer"
      @click="removeOptionsFromEnd(selectedOptions.length - maxVisibleItems)"
    >
      + {{ selectedOptions.length - maxVisibleItems }}
      <GwBtn
        icon="mdi-close"
        class="gw_hvr_content"
        variant="btn_alert_small"
        style="position: absolute; right: 0; transform: translate(50%, 0)"
      />
    </GwBox>
  </div>
</template>
